import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

// PageWrapper — single shell for every page under Layout.
//
// Layout already provides outer padding (px-4 py-8 / sm:px-6 / md:px-10) and
// a max-w-7xl outer container. Pages should NOT also stack their own mx-auto
// max-w-* — that double-wraps and creates the off-centre look the user
// flagged. Instead, every page returns:
//
//   <PageWrapper maxWidth="3xl" spacing="lg">…</PageWrapper>
//
// and PageWrapper does the inner mx-auto + width clamp + vertical rhythm.
//
// maxWidth tokens map to the few sizes the design uses across the app:
//   2xl (672px) — narrow text-heavy pages (My Card)
//   3xl (768px) — default for forms + Your Stuff sections
//   4xl (896px) — list-style admin pages (alerts, requests inbox)
//   5xl (1024px) — calendar / browse / 2-col admin
//   6xl (1152px) — wide dashboards (Command Center)
//   full        — page handles its own constraints (Match Card, OCR Lab)
//
// spacing tokens are the vertical-rhythm presets:
//   none — page handles its own gaps
//   sm   — space-y-4
//   md   — space-y-6   (default)
//   lg   — space-y-8

type MaxWidth = '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | 'full';
type Spacing = 'none' | 'sm' | 'md' | 'lg';

const MAX_WIDTH_CLASS: Record<MaxWidth, string> = {
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  full: '',
};

const SPACING_CLASS: Record<Spacing, string> = {
  none: '',
  sm: 'space-y-4',
  md: 'space-y-6',
  lg: 'space-y-8',
};

export function PageWrapper({
  children,
  maxWidth = '3xl',
  spacing = 'md',
  className,
}: {
  children: ReactNode;
  maxWidth?: MaxWidth;
  spacing?: Spacing;
  className?: string;
}) {
  return (
    <div className={cn('mx-auto w-full', MAX_WIDTH_CLASS[maxWidth], SPACING_CLASS[spacing], className)}>
      {children}
    </div>
  );
}
